import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Utensils, RefreshCw, Minimize2, ChevronLeft, ChevronRight } from 'lucide-react';
import { DateRangePicker, DateRange } from '../common/DateRangePicker';
import { useNutritionSummary } from '../../hooks/useNutritionSummary';
import { NutritionTable, WeightData } from './NutritionTable';
import { NutritionDayView } from './NutritionDayView';
import { NutritionGraph } from './NutritionGraph';
import { GoalTarget } from '../client/HealthCalendarTile';

/**
 * NUTRITION EXPANDED VIEW - Full viewport nutrition display
 *
 * Takes over the workspace area when NutritionTile is expanded.
 * The mini-sidebar is now provided by ClientSummaryView.
 * This component is just the main content area with header.
 *
 * ESC key closes the view.
 */

interface NutritionExpandedViewProps {
  clientId: string;
  clientName: string;
  clientInitials: string;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  onMonthChange?: (month: number, year: number) => void;
  onClose: () => void;
}

export const NutritionExpandedView: React.FC<NutritionExpandedViewProps> = ({
  clientId,
  clientName,
  clientInitials: _clientInitials,
  dateRange,
  onDateRangeChange,
  onMonthChange,
  onClose,
}) => {
  const [syncing, setSyncing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [highlightedDate, setHighlightedDate] = useState<string | null>(null);
  const [weightMap, setWeightMap] = useState<Map<string, WeightData>>(new Map());
  const [goalsMap, setGoalsMap] = useState<Map<string, Record<string, GoalTarget>>>(new Map());
  const { summary, connectionStatus, loading, error, triggerSync } = useNutritionSummary(
    clientId,
    dateRange
  );

  // Fetch weight data from daily metrics for the same date range
  const loadWeightData = useCallback(async () => {
    if (!clientId || !dateRange.startDate || !dateRange.endDate) return;
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Determine which months we need to cover
      const start = new Date(dateRange.startDate + 'T00:00:00');
      const end = new Date(dateRange.endDate + 'T00:00:00');
      const monthsToFetch: { month: number; year: number }[] = [];
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end) {
        monthsToFetch.push({ month: cursor.getMonth() + 1, year: cursor.getFullYear() });
        cursor.setMonth(cursor.getMonth() + 1);
      }

      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const responses = await Promise.all(
        monthsToFetch.map(m =>
          fetch(`/api/coach/clients/${clientId}/metrics/calendar?month=${m.month}&year=${m.year}`, { headers })
            .then(r => r.ok ? r.json() : { data: { healthData: [] } })
        )
      );

      // Merge all months, filter to date range, extract weight + goals
      const allDays: { date: string; weight: number | null; targets?: Record<string, GoalTarget> }[] = [];
      for (const res of responses) {
        const days = res.data?.healthData || res.healthData || [];
        for (const day of days) {
          if (day.date >= dateRange.startDate && day.date <= dateRange.endDate) {
            const w = day.actual_values?.weight ?? null;
            allDays.push({
              date: day.date,
              weight: w && w > 0 ? w : null,
              targets: day.targets_at_time || undefined,
            });
          }
        }
      }

      // Sort by date and compute deltas
      allDays.sort((a, b) => a.date.localeCompare(b.date));
      const map = new Map<string, WeightData>();
      const gMap = new Map<string, Record<string, GoalTarget>>();
      let prevWeight: number | null = null;
      for (const day of allDays) {
        const delta = (day.weight != null && prevWeight != null) ? day.weight - prevWeight : null;
        map.set(day.date, { weight: day.weight, delta });
        if (day.targets) gMap.set(day.date, day.targets);
        if (day.weight != null) prevWeight = day.weight;
      }
      setWeightMap(map);
      setGoalsMap(gMap);
    } catch (err) {
      console.error('Failed to load weight data for nutrition table:', err);
    }
  }, [clientId, dateRange.startDate, dateRange.endDate]);

  useEffect(() => { loadWeightData(); }, [loadWeightData]);

  // Handle date click from table - navigate to day view
  const handleDateClick = (date: string) => {
    setSelectedDate(date);
  };

  // Handle back from day view - return to summary
  const handleBackToSummary = () => {
    setSelectedDate(null);
  };

  // ESC key handling - priority: deselect highlighted → close day view → let parent close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      // First priority: deselect highlighted row/point
      if (highlightedDate) {
        e.stopImmediatePropagation();
        e.preventDefault();
        setHighlightedDate(null);
        return;
      }

      // Second priority: close day detail view
      if (selectedDate) {
        e.stopImmediatePropagation();
        e.preventDefault();
        setSelectedDate(null);
        return;
      }
      // Otherwise let the parent (ClientSummaryView) handle closing
    };

    // Use capture phase to run before other handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedDate, highlightedDate]);

  const handleSync = async () => {
    setSyncing(true);
    await toast.promise(triggerSync(), {
      loading: 'Syncing from FatSecret...',
      success: (result: any) => {
        const daysChecked = (result.syncedDays || 0) + (result.skippedDays || 0);
        const daysChanged = (result.syncedDays || 0) - (result.unchangedDays || 0);
        const detail = daysChanged > 0
          ? `${daysChanged} days updated`
          : 'Everything up to date';
        return `Nutrition Data Updated\nChecked ${daysChecked} days · ${detail}`;
      },
      error: 'Sync failed'
    });
    setSyncing(false);
  };

  // Month navigation helper
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const navigateMonth = (direction: -1 | 1) => {
    const current = new Date(dateRange.startDate + 'T00:00:00');
    const newMonth = new Date(current.getFullYear(), current.getMonth() + direction, 1);
    const lastDay = new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0);
    onDateRangeChange({ startDate: fmt(newMonth), endDate: fmt(lastDay), preset: 'custom' });
    onMonthChange?.(newMonth.getMonth(), newMonth.getFullYear());
  };

  const getMonthLabel = (): string => {
    const start = new Date(dateRange.startDate + 'T00:00:00');
    return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatLastSync = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="nutrition-expanded-view">
      {/* Header Bar */}
      <div className="expanded-header">
        <div className="header-left">
          <Utensils size={20} />
          <h2>Nutrition</h2>
          <span className="client-name-badge">{clientName}</span>
          {connectionStatus?.connected && (
            <span className="status-badge connected">FatSecret</span>
          )}
          {connectionStatus?.last_sync && (
            <span className="last-sync-label">
              Synced {formatLastSync(connectionStatus.last_sync)}
            </span>
          )}
        </div>
        <div className="header-right">
          <DateRangePicker value={dateRange} onChange={(range) => {
            onDateRangeChange(range);
            // Sync calendar to the start month of the selected range
            const start = new Date(range.startDate + 'T00:00:00');
            onMonthChange?.(start.getMonth(), start.getFullYear());
          }} />
          <div className="month-nav">
            <button className="month-nav-btn" onClick={() => navigateMonth(-1)} title="Previous month">
              <ChevronLeft size={16} />
            </button>
            <button className="month-nav-label" onClick={() => {
              const now = new Date();
              const first = new Date(now.getFullYear(), now.getMonth(), 1);
              const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              onDateRangeChange({ startDate: fmt(first), endDate: fmt(last), preset: 'custom' });
              onMonthChange?.(now.getMonth(), now.getFullYear());
            }} title="Go to current month">{getMonthLabel()}</button>
            <button className="month-nav-btn" onClick={() => navigateMonth(1)} title="Next month">
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            className="sync-btn"
            onClick={handleSync}
            disabled={syncing}
            title="Sync from FatSecret"
          >
            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
          </button>
          <button
            className="minimize-btn"
            onClick={onClose}
            title="Minimize (ESC)"
          >
            <Minimize2 size={16} />
          </button>
        </div>
      </div>

        {/* Content Area - Placeholder for Phase 9b/9c/9e */}
        <div className="expanded-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>Loading nutrition data...</p>
            </div>
          )}

          {error && !loading && (
            <div className="error-state">
              <p>Error loading nutrition data: {error}</p>
            </div>
          )}

          {!loading && !error && summary && summary.totals.days_with_data > 0 && (
            <div className="content-area">
              {selectedDate ? (
                /* Day Detail View - Phase 9c */
                <div className="day-view-area">
                  <NutritionDayView
                    clientId={clientId}
                    date={selectedDate}
                    onBack={handleBackToSummary}
                  />
                </div>
              ) : (
                <>
                  {/* Graph Area - Phase 9e */}
                  <div className="graph-area">
                    <NutritionGraph days={summary.days} highlightedDate={highlightedDate} onDateSelect={(d) => setHighlightedDate(d || null)} goalsData={goalsMap} />
                  </div>

                  {/* Nutrition Table - Phase 9b + 9c */}
                  <div className="table-area">
                    <NutritionTable
                      summary={summary}
                      onDateClick={handleDateClick}
                      onRowSelect={setHighlightedDate}
                      selectedDate={highlightedDate}
                      weightData={weightMap}
                      goalsData={goalsMap}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {!loading && !error && summary && summary.totals.days_with_data === 0 && (
            <div className="no-data-state">
              <Utensils size={48} />
              <h3>No Data Available</h3>
              <p>No nutrition data found for the selected date range.</p>
              <p>Try syncing from FatSecret or selecting a different date range.</p>
            </div>
          )}
        </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .nutrition-expanded-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 500px;
    background: transparent;
    animation: expandIn 0.2s ease-out;
  }

  @keyframes expandIn {
    from {
      opacity: 0;
      transform: scale(0.98);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Header */
  .expanded-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-secondary);
    border-radius: var(--radius-large) var(--radius-large) 0 0;
  }

  .expanded-header .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .expanded-header .header-left svg {
    color: var(--accent-green);
  }

  .expanded-header h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .client-name-badge {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 4px 10px;
    background: var(--bg-tertiary);
    border-radius: 12px;
  }

  .expanded-header .header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .minimize-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .minimize-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .status-badge {
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 10px;
  }

  .status-badge.connected {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  }

  .sync-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .sync-btn:hover:not(:disabled) {
    border-color: var(--accent-green);
    color: var(--accent-green);
  }

  .sync-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sync-btn .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .last-sync-label {
    font-size: 11px;
    color: var(--text-tertiary);
  }

  .month-nav {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-right: 4px;
  }

  .month-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-small, 4px);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
    padding: 0;
  }

  .month-nav-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border-color: var(--accent-green);
  }

  .month-nav-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    width: 85px;
    text-align: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 0;
    transition: color 0.15s;
  }

  .month-nav-label:hover {
    color: var(--accent-green);
  }

  /* Content Area */
  .expanded-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  .loading-state,
  .error-state,
  .no-data-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    text-align: center;
    color: var(--text-secondary);
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  .no-data-state svg {
    color: var(--text-tertiary);
    margin-bottom: 16px;
  }

  .no-data-state h3 {
    margin: 0 0 8px;
    font-size: 16px;
    color: var(--text-primary);
  }

  .no-data-state p {
    margin: 0;
    font-size: 13px;
  }

  /* Content Layout */
  .content-area {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
  }

  /* Graph Area - Phase 9e */
  .graph-area {
    flex-shrink: 0;
    height: 280px;
  }

  /* Table Area */
  .table-area {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /* Day View Area - Phase 9c */
  .day-view-area {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
`;

export default NutritionExpandedView;
